import React from 'react';
import { Add16Regular, Delete12Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { DeleteTaskLinkedRecord, GetTaskLinkedRecords } from '../../redux/taskSlice';
import TaskReferencePicker from './TaskReferencePicker';
import { htmlToPlainText } from '../../utils/commonUtils';
import styles from './css/index.module.css';

const formatReferenceText = text => {
    if (!text) return '';
    return String(htmlToPlainText(text) || '')
        .replace(/\s+/g, ' ')
        .trim();
};

const GROUP_ORDER = [
    { type: 'vdr_file', label: 'VDR Files' },
    { type: 'irl', label: 'IRLs' },
    { type: 'observation', label: 'Observations' },
];

const TaskReferences = ({
    taskId,
    onOpenLink,
    scrollToRequest = null,
    openPickerRequest = null,
    onOpenPickerRequestHandled,
    label = 'Reference',
    hideLabel = false,
}) => {
    const dispatch = useDispatch();
    const { taskLinkedRecords, taskLinkedRecordsLoading } = useSelector(state => state.task);
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const sectionRef = React.useRef(null);

    React.useEffect(() => {
        if (taskId) dispatch(GetTaskLinkedRecords({ taskId }));
    }, [taskId, dispatch]);

    React.useEffect(() => {
        if (scrollToRequest == null) return;
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        onOpenPickerRequestHandled?.();
    }, [scrollToRequest, onOpenPickerRequestHandled]);

    React.useEffect(() => {
        if (openPickerRequest == null) return;
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setPickerOpen(true);
        onOpenPickerRequestHandled?.();
    }, [openPickerRequest, onOpenPickerRequestHandled]);

    const grouped = React.useMemo(() => {
        const map = { vdr_file: [], irl: [], observation: [] };
        (taskLinkedRecords || []).forEach(link => {
            if (map[link.record_type]) map[link.record_type].push(link);
        });
        return map;
    }, [taskLinkedRecords]);

    const hasLinks = (taskLinkedRecords || []).length > 0;

    return (
        <div className={styles.attachmentsSection} ref={sectionRef}>
            {!hideLabel && <p className={styles.attachmentSectionLabel}>{label}</p>}
            <div className={styles.attachmentList}>
                {taskLinkedRecordsLoading && !hasLinks ? (
                    <p className={styles.referenceEmpty}>Loading…</p>
                ) : !hasLinks ? (
                    <p className={styles.referenceEmpty}>No linked records</p>
                ) : (
                    GROUP_ORDER.map(group => {
                        const items = grouped[group.type] || [];
                        if (!items.length) return null;
                        return (
                            <div key={group.type} className={styles.referenceGroup}>
                                <p className={styles.referenceGroupLabel}>{group.label}</p>
                                {items.map(link => {
                                    const label = formatReferenceText(link.display_label) || `Record ${link.record_id}`;
                                    const subtitle = formatReferenceText(link.display_subtitle);
                                    return (
                                    <div key={link.id} className={styles.referenceRow}>
                                        <button
                                            type="button"
                                            className={styles.referenceLinkBtn}
                                            onClick={() => onOpenLink?.(link)}
                                            title={subtitle || label}>
                                            <span className={styles.referenceLinkLabel}>{label}</span>
                                            {subtitle ? (
                                                <span className={styles.referenceLinkSubtitle}>{subtitle}</span>
                                            ) : null}
                                        </button>
                                        <div className={styles.attachmentRowActions}>
                                            <DoubleConfirmationModal
                                                message="Remove this reference from the task? The original record will not be deleted."
                                                handleAction={() => dispatch(DeleteTaskLinkedRecord({ linkId: link.id }))}>
                                                <button type="button" className={styles.attachmentRowActionBtn} title="Remove reference">
                                                    <Delete12Regular />
                                                </button>
                                            </DoubleConfirmationModal>
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        );
                    })
                )}
                <div className={styles.attachmentListFooter}>
                    <button type="button" className={styles.attachmentAddLink} onClick={() => setPickerOpen(true)}>
                        <Add16Regular />
                        <span>Add a reference</span>
                    </button>
                </div>
            </div>

            <TaskReferencePicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                taskId={taskId}
                existingLinks={taskLinkedRecords}
            />
        </div>
    );
};

export default TaskReferences;
